import InfoIcon from '@mui/icons-material/Info';
import { Grid, Tooltip } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import RGL, { WidthProvider } from 'react-grid-layout';
import { useSearchParams } from 'react-router-dom';
import {
  ComposableDashboardFiltersEnum,
  IComposableAnalyticsCharts,
  IComposableDashboardCollectionsContext,
  IComposableDashboardContext,
  IComposableDashboardFiltersContext,
  IComposableDynamicListingContext,
} from 'wisestep-api';
import { ScrollableTabs, WsColors, WsListingLoader } from 'wisestep-base';
import './rgl-styles.css';

import {
  composableChartsDrawerValues,
  jobsTimePeriodList,
} from '../../../../features/composableAnalytics';
import ErrorLogger from '../../../../providers/ErrorLogger';
import { businessDefinitionTimePeriodList } from '../../../composableAnalytics/DerivedCharts/businessDefinition/helper/postgreBusinessDefinitionKeys';
import { IFreeTextEditorProps } from '../../../composableAnalytics/Widgets/FreeTextEditor';
import { Index } from '../../../composableAnalytics/Widgets/helper/freeTextWidgetKeys';
import getTimePeriodLabelsFromKey from '../../helper/getTimePeriodLabelsFromKey';
import { useComposableDashboardCollectionsContext } from '../../hooks/useComposableDashboardCollectionsContext';
import { useComposableDashboardsContext } from '../../hooks/useComposableDashboardsContext';
import { useComposableDashboardsFiltersContext } from '../../hooks/useComposableDashboardsFiltersContext';
import { useComposableDynamicListingContext } from '../../hooks/useComposableDynamicListingContext';

import DashboardFilters from './DashboardFilters';

const ReactGridLayout = WidthProvider(RGL);

interface IRenderDashboardsTabsProps {
  showFilterTags?: boolean;
}

const RenderDashboardsTabs = ({ showFilterTags = true }: IRenderDashboardsTabsProps) => {
  const [params] = useSearchParams();
  const userEmail = params?.get('email');
  const { selectedDashboardCollection, setSelectedCollectionDashboardByIndex } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const { selectedDashboard, setSelectedDashboard } =
    useComposableDashboardsContext() as Required<IComposableDashboardContext>;
  const { refreshFiltersState, filters } =
    useComposableDashboardsFiltersContext() as Required<IComposableDashboardFiltersContext>;
  const {
    businessDefinitionChartsKeysPostgre,
    potentialRevenueForBDefChartsKeys,
    leadByStatusChartsKeys,
  } = useComposableDynamicListingContext() as Required<IComposableDynamicListingContext>;

  const businessDefinitionChartsKeysPostgreMemo = useMemo(
    () =>
      businessDefinitionTimePeriodList.flatMap((timePeriod) =>
        businessDefinitionChartsKeysPostgre(timePeriod)
      ),
    [businessDefinitionChartsKeysPostgre]
  );
  const potentialRevenueForBDefChartsKeysMemo = useMemo(
    () => jobsTimePeriodList.flatMap((timePeriod) => potentialRevenueForBDefChartsKeys(timePeriod)),
    [potentialRevenueForBDefChartsKeys]
  );
  const leadByStatusChartsKeysMemo = useMemo(
    () => jobsTimePeriodList.flatMap((timePeriod) => leadByStatusChartsKeys(timePeriod)),
    [leadByStatusChartsKeys]
  );
  const [selectedTab, setSelectedTab] = useState<number>(-1);
  const dashboardsList = selectedDashboardCollection?.dashboards?.map((dashboard, index) => ({
    tabName: dashboard?.name,
    index: index,
  }));
  const chartsToRender = (selectedDashboard?.charts ?? []) as IComposableAnalyticsCharts[];

  const handleSelectedTab = useCallback(
    (index: number) => {
      setSelectedTab(index == -1 ? 0 : index);
      setSelectedCollectionDashboardByIndex(index, refreshFiltersState);
    },
    [refreshFiltersState, setSelectedCollectionDashboardByIndex]
  );
  useEffect(() => {
    if (dashboardsList?.length) {
      handleSelectedTab(0);
    } else handleSelectedTab(-1);
  }, [dashboardsList?.length, handleSelectedTab]);

  const handleNotFoundCase = (chartKey: string) => {
    const updatedArray = chartsToRender.filter((chartKeyObj) => chartKeyObj.type !== chartKey);
    setSelectedDashboard((prev) => {
      prev.charts = updatedArray;
      return { ...prev };
    });
  };

  const generateChart = ({
    type: chartKey,
    layout,
    id: widgetId,
    text,
  }: IComposableAnalyticsCharts) => (
    <Grid
      key={widgetId}
      data-grid={layout}
      bgcolor={WsColors.TheBlues25}
      margin="auto"
      p={1}
      overflow="hidden"
      borderRadius={1}
      boxShadow="0px 2px 4px rgba(0, 0, 0, 0.1)"
      position="relative"
    >
      {chartKey != Index.FREE_TEXT_WIDGET && (
        <Tooltip title={getTimePeriodLabelsFromKey(chartKey, filters)} placement="left">
          <InfoIcon
            sx={{
              position: 'absolute',
              top: '0.4rem',
              right: '1.7rem',
              zIndex: 1,
              color: WsColors.TertiaryShades300,
            }}
            fontSize="small"
          />
        </Tooltip>
      )}
      <Grid height="95%">
        {(() => {
          const Widget = [
            ...composableChartsDrawerValues,
            ...businessDefinitionChartsKeysPostgreMemo,
            ...potentialRevenueForBDefChartsKeysMemo,
            ...leadByStatusChartsKeysMemo,
          ].find((chartKeyObj) => chartKeyObj.type === chartKey)?.component;

          if (!Widget) {
            handleNotFoundCase(chartKey);
            return null;
          }
          if (chartKey === Index.FREE_TEXT_WIDGET) {
            const FreeTextEditor = Widget as React.FC<IFreeTextEditorProps>;
            return <FreeTextEditor text={text} id={widgetId} dashboardEditMode={false} />;
          } else return Widget;
        })()}
      </Grid>
    </Grid>
  );
  return (
    <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
      <Grid p={1}>
        <Grid item xs={12}>
          {dashboardsList && (
            <ScrollableTabs
              scrollList={dashboardsList ?? []}
              getTabIndex={handleSelectedTab}
              divider={false}
            />
          )}
        </Grid>
        <DashboardFilters
          disableFiltersList={userEmail ? [ComposableDashboardFiltersEnum.RECRUITER] : []}
          userEmail={userEmail ?? ''}
          showFilterTags={showFilterTags}
        />
        {selectedTab >= 0 && (
          <>
            {!isEmpty(chartsToRender) ? (
              <Grid bgcolor={WsColors.White}>
                <ReactGridLayout className="layout" isDraggable={false} isResizable={false}>
                  {chartsToRender?.map((chart) => generateChart(chart))}
                </ReactGridLayout>
              </Grid>
            ) : (
              <WsListingLoader />
            )}
          </>
        )}
      </Grid>
    </ErrorBoundary>
  );
};
export default RenderDashboardsTabs;
